function isReady(sample) {
  return sample?.has_test_measurements?.toUpperCase() === "YES";
}

function clean(value) {
  return String(value || "").trim();
}

function normalizeText(value) {
  return clean(value).toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

function normalizeCompact(value) {
  return clean(value).toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function normalizeVin(value) {
  return clean(value).toUpperCase().replace(/[^A-Z0-9]/g, "");
}

function num(value) {
  if (value === null || value === undefined || value === "" || value === "NaN") return null;
  const parsed = parseFloat(String(value).replace(/,\s*/g, "").replace(/\s*K$/i, ""));
  return Number.isNaN(parsed) ? null : parsed;
}

function sampleTime(sample) {
  const raw = sample?.sample_tested_date || sample?.response_submission_timestamp || "";
  const time = raw ? new Date(raw).getTime() : 0;
  return Number.isNaN(time) ? 0 : time;
}

function compareByDateDesc(a, b) {
  return sampleTime(b) - sampleTime(a);
}

function vehicleLabelFromSample(sample) {
  const vehicleName = clean(sample?.vehicle_name);
  if (vehicleName) return vehicleName;
  const parts = [sample?.vehicle_year, sample?.vehicle_make, sample?.vehicle_model].map(clean).filter(Boolean);
  return parts.length > 0 ? parts.join(" ") : "Unknown Vehicle";
}

function vehicleTextKey(sample) {
  const vehicleName = normalizeCompact(sample?.vehicle_name);
  if (vehicleName) return `name:${vehicleName}`;
  const parts = [sample?.vehicle_year, sample?.vehicle_make, sample?.vehicle_model]
    .map(normalizeCompact)
    .filter(Boolean);
  return parts.length >= 2 ? `ymm:${parts.join(":")}` : "";
}

function hasStrongVehicleTextMatch(a, b) {
  const keyA = vehicleTextKey(a);
  const keyB = vehicleTextKey(b);
  if (keyA && keyB && keyA === keyB) return true;

  const makeA = normalizeCompact(a?.vehicle_make);
  const makeB = normalizeCompact(b?.vehicle_make);
  const modelA = normalizeCompact(a?.vehicle_model);
  const modelB = normalizeCompact(b?.vehicle_model);
  const yearA = normalizeCompact(a?.vehicle_year);
  const yearB = normalizeCompact(b?.vehicle_year);
  if (makeA && makeB && modelA && modelB && makeA === makeB && modelA === modelB) {
    return !yearA || !yearB || yearA === yearB;
  }

  const nameA = normalizeText(a?.vehicle_name);
  const nameB = normalizeText(b?.vehicle_name);
  return Boolean(nameA && nameB && (nameA.includes(nameB) || nameB.includes(nameA)));
}

function editDistanceWithinOne(a, b) {
  if (!a || !b) return false;
  if (Math.abs(a.length - b.length) > 1) return false;
  let edits = 0;
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      i += 1;
      j += 1;
      continue;
    }
    edits += 1;
    if (edits > 1) return false;
    if (a.length > b.length) i += 1;
    else if (b.length > a.length) j += 1;
    else {
      i += 1;
      j += 1;
    }
  }
  return edits + (a.length - i) + (b.length - j) <= 1;
}

function hasSimilarVin(a, b) {
  const vinA = normalizeVin(a?.vehicle_vin);
  const vinB = normalizeVin(b?.vehicle_vin);
  if (!vinA || !vinB) return false;
  if (vinA === vinB) return true;
  if (vinA.length >= 8 && vinB.length >= 8 && vinA.slice(-8) === vinB.slice(-8)) return true;
  return editDistanceWithinOne(vinA, vinB);
}

function hasMileageConflict(a, b) {
  const milesA = num(a?.engine_miles);
  const milesB = num(b?.engine_miles);
  if (milesA === null || milesB === null) return false;
  const timeA = sampleTime(a);
  const timeB = sampleTime(b);
  if (!timeA || !timeB || timeA === timeB) return false;
  return timeA < timeB ? milesA > milesB : milesB > milesA;
}

function samplesMatchVehicle(a, b) {
  const vinA = normalizeVin(a?.vehicle_vin);
  const vinB = normalizeVin(b?.vehicle_vin);

  if (vinA && vinB && vinA === vinB) {
    return { match: true, reason: "exact-vin" };
  }

  if (vinA && vinB) {
    if (hasSimilarVin(a, b) && hasStrongVehicleTextMatch(a, b) && !hasMileageConflict(a, b)) {
      return { match: true, reason: "similar-vin-text-mileage" };
    }
    return { match: false, reason: "different-vin" };
  }

  if (hasStrongVehicleTextMatch(a, b) && !hasMileageConflict(a, b)) {
    return { match: true, reason: "vehicle-text-mileage" };
  }

  return { match: false, reason: "insufficient-evidence" };
}

function summarizeGroup(group) {
  group.samples.sort(compareByDateDesc);
  group.readySamples = group.samples.filter(isReady);
  group.pendingSamples = group.samples.filter((sample) => !isReady(sample));
  group.latestReadySample = group.readySamples[0] || null;
  group.latestSample = group.samples[0] || null;
  group.label = vehicleLabelFromSample(group.latestSample);
  const reportCount = group.samples.length;
  const pendingCount = group.pendingSamples.length;
  group.subtitle = `${reportCount} report${reportCount === 1 ? "" : "s"}${pendingCount ? `, ${pendingCount} pending` : ""}`;
  return group;
}

function createGroup(sample, index, forcedId) {
  const vin = normalizeVin(sample.vehicle_vin);
  const key = vehicleTextKey(sample) || `sample:${sample.lab_sample_id || index}`;
  return summarizeGroup({
    id: forcedId || (vin ? `vin:${vin}` : `vehicle:${key}`),
    label: vehicleLabelFromSample(sample),
    subtitle: "",
    samples: [sample],
    readySamples: [],
    pendingSamples: [],
    latestReadySample: null,
    latestSample: sample,
    matchNotes: [`seed:${sample.lab_sample_id || index}`],
  });
}

function groupSamplesByVehicle(samples) {
  const groups = [];
  const orderedSamples = [...(samples || [])].sort(compareByDateDesc);

  orderedSamples.forEach((sample, index) => {
    const overrideId = clean(sample?.vehicle_override_id);
    if (overrideId) {
      const overrideGroup = groups.find((group) => group.id === overrideId);
      if (overrideGroup) {
        overrideGroup.samples.push(sample);
        overrideGroup.matchNotes.push(`override:${sample.lab_sample_id || index}`);
        summarizeGroup(overrideGroup);
      } else {
        groups.push(createGroup(sample, index, overrideId));
      }
      return;
    }

    const matchedGroup = groups.find((group) =>
      group.samples.some((existing) => samplesMatchVehicle(existing, sample).match)
    );

    if (!matchedGroup) {
      groups.push(createGroup(sample, index));
      return;
    }

    const reason = matchedGroup.samples
      .map((existing) => samplesMatchVehicle(existing, sample))
      .find((result) => result.match)?.reason || "matched";
    matchedGroup.samples.push(sample);
    matchedGroup.matchNotes.push(`${reason}:${sample.lab_sample_id || index}`);
    summarizeGroup(matchedGroup);
  });

  return groups
    .map(summarizeGroup)
    .sort((a, b) => sampleTime(b.latestSample) - sampleTime(a.latestSample));
}

module.exports = {
  groupSamplesByVehicle,
  normalizeVin,
  samplesMatchVehicle,
  vehicleLabelFromSample,
  sampleTime,
  isReady,
};
